/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode } from "preact";
import {
  AccessToken,
  HttpStatusCode,
  LoginTokenScope,
} from "@gnu-taler/taler-util";
import { merchantClient } from "../api/client.js";
import { normalizeToken } from "../stores/session.js";
import { loginTokenLifetimeMinutes } from "../stores/devSettings.js";
import {
  handleRouteSendChallenge,
  handleRouteSolveChallenge,
} from "../utils/tan.js";
import {
  SignInScreen,
  type SignInAttempt,
  type SignInScreenProps,
} from "../screens/SignInScreen.js";
import { configurationFailure, normalizeApiFailure } from "../api/failure.js";
import { useTranslation, type TranslateFn } from "../context/translation.js";

/**
 * Whether what was typed is already a credential rather than a password.
 *
 * The field takes either, and someone pasting a token they were given has
 * nothing to exchange.
 */
function looksLikeToken(typed: string): boolean {
  return typed.startsWith("secret-token:") || typed.startsWith("st_");
}

/**
 * The part of a failure worth showing or acting on.
 *
 * A documented failure carries `detail`, but an *alternative* one — a 202
 * demanding a second factor — carries a codec-parsed `body` instead, and that
 * is where the challenges are. Reading only `detail` loses the challenge list
 * and strands the merchant on the password step.
 */
function failureDetail(res: unknown): unknown {
  const r = res as { detail?: unknown; body?: unknown };
  return r.detail ?? r.body;
}

export async function authenticate(
  input: {
    account: string;
    secret: string;
    backendUrl: string;
    /**
     * Challenges already solved in this flow.
     *
     * Sign-up solves its second factor while creating the account, and the token
     * request that follows would otherwise demand a fresh one — leaving the
     * merchant waiting for a code that the screen never asked to be sent.
     */
    challengeIds?: string[];
  },
  t?: TranslateFn,
): Promise<SignInAttempt> {
  const { account, secret, backendUrl, challengeIds } = input;

  let rootUrl: URL;
  try {
    rootUrl = new URL(backendUrl);
  } catch (cause) {
    return {
      type: "fail",
      failure: configurationFailure(
        t
          ? t`The merchant backend URL is invalid.`
          : "The merchant backend URL is invalid.",
        cause,
      ),
    };
  }

  const client = merchantClient({ rootUrl, account });

  try {
    // Exchange the password for a login token rather than using it as the
    // credential. api-merchant.rst 4.1 deprecated the latter in v19: a bare
    // "secret-token:$INSTANCE_PASSWORD" still authenticates, but "will be
    // phased out in favor of login tokens", and it meant the password itself
    // was kept in local storage and sent with every request.
    if (looksLikeToken(secret)) {
      const formatted = normalizeToken(secret);
      // Nothing to exchange, so the credential is checked by using it.
      const res = await client.listOrders(formatted as AccessToken, {});
      if (res.type === "ok") {
        // No expiry and no renewal: a pasted credential is not ours to manage.
        return { type: "ok", token: formatted };
      }
      // A password is allowed to begin with the token prefix. Only an
      // authoritative authentication rejection permits the password exchange;
      // transport and server failures must not trigger a second mutation.
      const responseStatus = res.case as number;
      if (
        responseStatus !== HttpStatusCode.Unauthorized &&
        responseStatus !== HttpStatusCode.Forbidden
      ) {
        return {
          type: "fail",
          case: res.case as number,
          detail: failureDetail(res),
          failure: normalizeApiFailure(res),
        };
      }
    }

    const res = await client.createAccessToken(
      account,
      secret,
      {
        // "spa" is the scope for this portal; refreshable so the session can be
        // extended without asking for the password again.
        scope: LoginTokenScope.All_Refreshable,
        duration: { d_us: loginTokenLifetimeMinutes.value * 60 * 1_000_000 },
        description: t ? t`Merchant portal sign-in` : "Merchant portal sign-in",
      },
      challengeIds && challengeIds.length > 0 ? { challengeIds } : {},
    );

    if (res.type !== "ok") {
      return {
        type: "fail",
        case: res.case as number,
        detail: failureDetail(res),
        failure: normalizeApiFailure(res),
      };
    }

    // The backend may grant less than was asked for, so what it actually
    // returned is what gets stored. access_token already carries the prefix.
    const expiresS = res.body.expiration?.t_s;
    return {
      type: "ok",
      token: res.body.access_token,
      tokenInfo: {
        expiresS: typeof expiresS === "number" ? expiresS : undefined,
        refreshable: res.body.refreshable === true,
        scope: res.body.scope,
      },
    };
  } catch (cause) {
    // Never sign in on a failure to reach the server. This used to complete the
    // sign-in anyway, with whatever was typed — so a network blip stored the
    // merchant's password as their credential. An absent `case` says the
    // request never landed, which the screen reports as a connection problem.
    return { type: "fail", failure: normalizeApiFailure(cause) };
  }
}
export function SignInRoute(props: SignInScreenProps): VNode {
  const { t } = useTranslation();
  return (
    <SignInScreen
      {...props}
      onAuthenticate={(input) => authenticate(input, t)}
      onSendChallenge={(challengeId, target) =>
        handleRouteSendChallenge(challengeId, target, t)
      }
      onSolveChallenge={(challengeId, code, target) =>
        handleRouteSolveChallenge(challengeId, code, target, t)
      }
    />
  );
}
